'use client';

const MAX_DIM = 1280;
const MAX_BYTES = 280_000;

/**
 * Captures the current browser tab as a compressed WebP data URL.
 * Returns null on failure (user cancelled, API unavailable, etc.).
 * Best-effort — callers should treat null as a no-op.
 */
export async function captureScreenshot(): Promise<string | null> {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' } as MediaTrackConstraints,
      preferCurrentTab: true,
    } as DisplayMediaStreamOptions);

    const track = stream.getVideoTracks()[0];
    const imageCapture = new ((window as unknown as Record<string, unknown>).ImageCapture as new (
      track: MediaStreamTrack,
    ) => { grabFrame(): Promise<ImageBitmap> })(track);
    const bitmap: ImageBitmap = await imageCapture.grabFrame();
    stream.getTracks().forEach((t) => t.stop());

    let w = bitmap.width;
    let h = bitmap.height;
    if (w > MAX_DIM || h > MAX_DIM) {
      const scale = MAX_DIM / Math.max(w, h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h);

    let quality = 0.7;
    let dataUrl = canvas.toDataURL('image/webp', quality);
    while (dataUrl.length > MAX_BYTES && quality > 0.15) {
      quality -= 0.1;
      dataUrl = canvas.toDataURL('image/webp', quality);
    }
    return dataUrl;
  } catch (err) {
    console.error('Screenshot capture failed:', err);
    return null;
  }
}
